import CopyButton from "@components/buttons/CopyButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import TruncatedCopyValue from "@components/rowcomponents/TruncatedCopyValue";
import { primeServices } from "@services/apis/Prime";
import {
  GetPrimeCadTradeDetailsData,
  GetPrimeCadTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeCadTradeDetailsModel";
import React, { useEffect, useState } from "react";
import webSocketService from "../../services/websocket/WebsocketServices";
import InvertibleDateTime from "./components/InvertibleDateTime";
import InvertibleRate from "./components/InvertibleRate";
import PrimeOrderFlowDetailsSkeleton from "./components/PrimeOrderFlowDetailsSkeleton";
import {
  getCanadaEventPillClass,
  getSettlementTypePill,
  getTransferTypePill,
  OrderDetailsPills,
} from "./components/PrimeOrderPills";
import RedirectButton from "../Transactions/components/RedirectButton";

interface PrimeCanadaOrderFlowDetailsProps {
  instanceId: string;
}

export const PrimeCanadaOrderFlowDetails: React.FC<
  PrimeCanadaOrderFlowDetailsProps
> = ({ instanceId }) => {
  const [data, setData] = useState<GetPrimeCadTradeDetailsData>();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response: GetPrimeCadTradeDetailsResponse =
        await primeServices.getCadInstanceDetails({ instanceId });

      const processedData = {
        ...response.data,
      };

      setData(processedData);
    } catch (error: any) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [instanceId]);

  const handleTradeChangeWebSockets = (
    socketData: GetPrimeCadTradeDetailsData,
  ) => {
    if (socketData.instanceId === instanceId) {
      const processedData = {
        ...socketData,
      };
      setData(processedData);
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.onMessage(
          "cad_instance_details",
          true,
          handleTradeChangeWebSockets,
        );
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };

    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected]);

  if (error) {
    return <div>Fetch error</div>;
  }

  if (isLoading) {
    return <PrimeOrderFlowDetailsSkeleton width="w-full" />;
  }

  const tradeTypeLabel = data?.isBuying === 1 ? "BUY" : "SELL";

  const settlementDetails = data?.cryptoSettlementDetails;
  const transferPill = getTransferTypePill(settlementDetails?.transferType);
  const settlementPill = getSettlementTypePill(
    settlementDetails?.settlementType,
  );
  const settlementHash = settlementDetails?.transactionHash?.trim() || "";
  const settlementNetwork = settlementDetails?.network?.trim() || "";

  return (
    <div
      className="flex flex-col gap-[12px] mt-[20px]"
      style={{ height: "65dvh" }}
    >
      <div className="flex flex-col py-[8px] px-[12px] space-y-[16px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-col gap-3">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Merchant Details:
          </p>
          <LabelRow
            label="Merchant ID | User ID"
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal">
                  {data?.merchantId}
                </span>
                <CopyButton
                  copyText={data?.merchantId?.toString() ?? ""}
                  copyMessage="Copied to clipboard"
                />
                <span className="text-text-base text-[12px] font-normal">
                  |
                </span>
                <span className="text-text-base text-[12px] font-normal">
                  {data?.userId}
                </span>
                <CopyButton
                  copyText={(data?.userId || "").toString()}
                  copyMessage="Copied to clipboard"
                />
              </div>
            }
          />
          <LabelRow label="Merchant Name" value={data?.merchantName} />
        </div>
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Sub Merchant Details:
        </p>

        <LabelRow
          label="Merchant Name"
          value={
            data?.merchantDetails?.merchantName ??
            data?.merchantDetails?.merchantName ??
            "- -"
          }
          isCopy={
            !!data?.merchantDetails?.merchantName ||
            !!data?.merchantDetails?.merchantName
          }
        />

        <LabelRow
          label="Merchant Domain"
          value={
            data?.merchantDetails?.domain ??
            data?.merchantDetails?.merchantUrl ??
            "- -"
          }
          isCopy={
            !!data?.merchantDetails?.domain ||
            !!data?.merchantDetails?.merchantUrl
          }
        />
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[16px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-navbar-active text-[12px] font-semibold underline">
              Order Details:
            </p>
            <OrderDetailsPills
              tradeType={tradeTypeLabel}
              eventId={data?.eventId}
              eventClassName={getCanadaEventPillClass(data?.eventId)}
            />
          </div>

          <LabelRow
            label="Merchant Ref ID"
            value={data?.merchantRefId ?? ""}
            valueComp={
              <TruncatedCopyValue
                value={data?.merchantRefId}
                copyMessage="Merchant Ref ID copied to clipboard"
              />
            }
          />
          <LabelRow
            label="Instance ID"
            value={data?.instanceId ?? ""}
            isCopy={!!data?.instanceId}
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal">
                  {data?.instanceId && data?.instanceId.substring(0, 8)}...
                </span>
                <CopyButton
                  copyText={data?.instanceId ?? ""}
                  copyMessage="Copied to clipboard"
                />
              </div>
            }
          />
          <LabelRow
            label="Bank Reference"
            value={data?.interacReferenceNumber ?? "- -"}
            isCopy={!!data?.interacReferenceNumber}
          />
          <InvertibleDateTime value={data?.createdAt} />
        </div>
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Settlement Details:
        </p>
        <LabelRow
          label="Network"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              {settlementDetails?.networkImage && (
                <img
                  src={settlementDetails.networkImage}
                  height={12}
                  width={12}
                  alt=""
                />
              )}
              <span className="text-text-base text-[12px] font-normal">
                {settlementDetails?.network ?? "- -"}
              </span>
            </div>
          }
        />
        <LabelRow
          label="Transfer Type"
          valueComp={
            <span
              className={`flex text-[10px] h-[20px] px-[8px] font-bold text-center items-center rounded-lg ${transferPill.className}`}
            >
              {transferPill.text}
            </span>
          }
        />
        <LabelRow
          label="Wallet Address"
          value={settlementDetails?.walletAddress ?? ""}
          valueComp={
            <TruncatedCopyValue
              value={settlementDetails?.walletAddress}
              copyMessage="Wallet address copied to clipboard"
            />
          }
        />
        <LabelRow
          label="Settlement Type"
          valueComp={
            <span
              className={`flex text-[10px] h-[20px] px-[8px] font-bold text-center items-center rounded-lg ${settlementPill.className}`}
            >
              {settlementPill.text}
            </span>
          }
        />
        <LabelRow
          label="Transaction Hash"
          valueComp={
            settlementHash ? (
              <div className="flex flex-row items-center gap-[4px]">
                <TruncatedCopyValue
                  value={settlementHash}
                  copyMessage="Transaction hash copied to clipboard"
                />
                {settlementNetwork && (
                  <RedirectButton
                    transactionHash={settlementHash}
                    network={settlementNetwork}
                    fillColor=""
                  />
                )}
              </div>
            ) : (
              <span className="text-text-base text-[12px] font-normal">
                - -
              </span>
            )
          }
        />
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Trade Details:
        </p>
        <LabelRow
          label="Token | Fiat"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">
                {data?.cryptoCurrencySymbol}
              </span>
              <span className="text-text-base text-[12px] font-normal">|</span>
              <img src={data?.fiatCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">
                {data?.fiatCurrencySymbol}
              </span>
            </div>
          }
        />
        <LabelRow
          label="Amount in VDA | Fiat"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.cryptoAmount ?? 0).toFixed(2)}
              </span>
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">|</span>
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.fiatAmount ?? 0).toFixed(2)}
              </span>
              <img src={data?.fiatCurrencyImage} height={12} width={12} />
            </div>
          }
        />
        <LabelRow
          label="Rate"
          valueComp={
            <InvertibleRate
              cryptoToFiatRate={
                data?.rate != null ? Number(data.rate) : undefined
              }
              cryptoImage={data?.cryptoCurrencyImage}
              cryptoSymbol={data?.cryptoCurrencySymbol}
              fiatImage={data?.fiatCurrencyImage}
              fiatSymbol={data?.fiatCurrencySymbol}
            />
          }
        />
        <LabelRow
          label="Fees"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.fees || 0).toFixed(2)}
              </span>
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
            </div>
          }
        />
        <LabelRow
          label="MDR"
          value={`${Number(data?.MDR || 0).toFixed(2)} %`}
        />
        <LabelRow
          label="Amount to be released"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.toReleaseAmount || 0).toFixed(2)}
              </span>
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
            </div>
          }
        />
      </div>
    </div>
  );
};

export default PrimeCanadaOrderFlowDetails;
